import { useEffect, useState, type DragEvent } from "react";

import type { Link, ScheduledActivity } from "@/api";
import { LinkIcon } from "@/components/link-chips";
import { readableText } from "@/lib/color";
import { cn } from "@/lib/utils";

export const TEMPLATE_DRAG_TYPE = "application/x-leno-template";
export type TemplateDragPayload = { template_id: string; label_id: string };

const HOUR_PX = 56;
const MIN_BLOCK_PX = 22;
const SNAP_MINUTES = 15;

function minutesOfDay(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

/** Local midnight of a YYYY-MM-DD date. */
function localMidnight(date: string): Date {
  const [y, m, d] = date.split("-").map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

type Placed = ScheduledActivity & {
  top: number;
  height: number;
  lane: number;
  lanes: number;
};

/** Greedy lane packing so overlapping blocks sit side by side. */
function place(
  activities: ScheduledActivity[],
  startMin: number,
  endMin: number,
): Placed[] {
  const rows = activities
    .map((a) => {
      const s = new Date(a.start_at);
      const e = new Date(a.end_at);
      return {
        a,
        s: minutesOfDay(s),
        e: minutesOfDay(e) === 0 && e > s ? 24 * 60 : minutesOfDay(e),
      };
    })
    .filter(({ s, e }) => e > s && e > startMin && s < endMin)
    .sort((x, y) => x.s - y.s || y.e - x.e);

  // Lanes are assigned per cluster of mutually overlapping blocks, so a block
  // with nothing beside it keeps the full width.
  const placed: Placed[] = [];
  let cluster: typeof rows = [];
  let clusterEnd = -1;
  const flush = () => {
    const laneEnds: number[] = [];
    const laneOf = cluster.map(({ s, e }) => {
      let lane = laneEnds.findIndex((end) => end <= s);
      if (lane === -1) {
        lane = laneEnds.length;
        laneEnds.push(e);
      } else {
        laneEnds[lane] = e;
      }
      return lane;
    });
    const lanes = Math.max(1, laneEnds.length);
    cluster.forEach(({ a, s, e }, i) => {
      const top = ((Math.max(s, startMin) - startMin) / 60) * HOUR_PX;
      const height = Math.max(
        MIN_BLOCK_PX,
        ((Math.min(e, endMin) - Math.max(s, startMin)) / 60) * HOUR_PX,
      );
      placed.push({ ...a, top, height, lane: laneOf[i], lanes });
    });
    cluster = [];
  };
  for (const row of rows) {
    if (cluster.length > 0 && row.s >= clusterEnd) flush();
    cluster.push(row);
    clusterEnd = Math.max(clusterEnd, row.e);
  }
  if (cluster.length > 0) flush();
  return placed;
}

/**
 * The waking-day timeline (business-requirements.md § Today). Hourly rows
 * from Day Start (floored) to Day End (rounded up), a "now" line while the
 * current time is inside the window, Activities as coloured blocks with a
 * minimum height. Blocks are not editable here; times change on Google
 * Calendar. Dropping a Template on the grid schedules it at that time.
 */
export function Timeline({
  date,
  dayStartMinutes,
  dayEndMinutes,
  activities,
  onDropTemplate,
  linksByTemplate,
  onPastClick,
  onFutureClick,
}: {
  date: string;
  dayStartMinutes: number;
  dayEndMinutes: number;
  activities: ScheduledActivity[];
  onDropTemplate?: (start: Date, payload: TemplateDragPayload) => void;
  /** Links per Template id, shown as an icon on blocks scheduled from it. */
  linksByTemplate?: Map<string, Link[]>;
  /** Clicking an ended block Leno scheduled calls this. */
  onPastClick?: (activity: ScheduledActivity) => void;
  /** Click a block that has not started: refine it into a checklist. */
  onFutureClick?: (activity: ScheduledActivity) => void;
}) {
  const startHour = Math.floor(dayStartMinutes / 60);
  const endHour = Math.max(startHour + 1, Math.ceil(dayEndMinutes / 60));
  const startMin = startHour * 60;
  const endMin = endHour * 60;
  const hours = Array.from(
    { length: endHour - startHour },
    (_, i) => startHour + i,
  );
  const height = hours.length * HOUR_PX;

  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  const isToday = now.toDateString() === localMidnight(date).toDateString();
  const nowMin = minutesOfDay(now);
  const showNow = isToday && nowMin >= startMin && nowMin <= endMin;

  const [dragOverMin, setDragOverMin] = useState<number | null>(null);

  function minutesAt(ev: DragEvent<HTMLDivElement>): number {
    const rect = ev.currentTarget.getBoundingClientRect();
    const y = Math.min(rect.height, Math.max(0, ev.clientY - rect.top));
    const raw = startMin + (y / HOUR_PX) * 60;
    return Math.min(
      endMin - SNAP_MINUTES,
      Math.round(raw / SNAP_MINUTES) * SNAP_MINUTES,
    );
  }

  function handleDragOver(ev: DragEvent<HTMLDivElement>) {
    if (!onDropTemplate || !ev.dataTransfer.types.includes(TEMPLATE_DRAG_TYPE))
      return;
    ev.preventDefault();
    ev.dataTransfer.dropEffect = "copy";
    setDragOverMin(minutesAt(ev));
  }

  function handleDrop(ev: DragEvent<HTMLDivElement>) {
    if (!onDropTemplate) return;
    const raw = ev.dataTransfer.getData(TEMPLATE_DRAG_TYPE);
    setDragOverMin(null);
    if (!raw) return;
    ev.preventDefault();
    const payload = JSON.parse(raw) as TemplateDragPayload;
    const start = localMidnight(date);
    start.setMinutes(minutesAt(ev));
    onDropTemplate(start, payload);
  }

  const placed = place(activities, startMin, endMin);
  const fmt = (min: number) =>
    `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;

  return (
    <div className="flex text-sm">
      <div className="w-12 shrink-0 select-none" style={{ height }} aria-hidden>
        {hours.map((h) => (
          <div
            key={h}
            className="relative text-right text-xs text-muted-foreground"
            style={{ height: HOUR_PX }}
          >
            <span className="absolute -top-2 right-2">
              {String(h).padStart(2, "0")}:00
            </span>
          </div>
        ))}
      </div>
      <div
        className={cn(
          "relative flex-1 rounded-lg border border-border bg-card",
          dragOverMin !== null && "ring-2 ring-ring",
        )}
        style={{ height }}
        onDragOver={handleDragOver}
        onDragLeave={() => setDragOverMin(null)}
        onDrop={handleDrop}
        role="list"
        aria-label="Timeline"
      >
        {hours.map((h, i) => (
          <div
            key={h}
            className={cn(
              "absolute inset-x-0 border-t border-border/70",
              i === 0 && "border-t-0",
            )}
            style={{ top: i * HOUR_PX }}
            aria-hidden
          />
        ))}
        {placed.map((a) => {
          const width = 100 / a.lanes;
          const text = readableText(a.color_hex);
          const pastClickable =
            a.reschedulable === true && onPastClick !== undefined;
          const futureClickable =
            !a.external &&
            onFutureClick !== undefined &&
            new Date(a.start_at).getTime() > now.getTime();
          const clickable = pastClickable || futureClickable;
          const onActivate = () =>
            pastClickable ? onPastClick?.(a) : onFutureClick?.(a);
          return (
            <div
              key={a.id}
              role={clickable ? "button" : "listitem"}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? onActivate : undefined}
              onKeyDown={
                clickable
                  ? (ev) => {
                      if (ev.key === "Enter" || ev.key === " ") {
                        ev.preventDefault();
                        onActivate();
                      }
                    }
                  : undefined
              }
              title={`${pastClickable ? "Click to reschedule. " : ""}${futureClickable ? (a.refined ? "Planned: click to edit the steps. " : "Click to plan the steps. ") : ""}${a.missed ? "Missed (no free slot). " : ""}${a.external ? "(not yours) " : ""}${a.title} ${new Date(a.start_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}–${new Date(a.end_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
              className={cn(
                "absolute overflow-hidden rounded-md px-2 py-1 text-xs leading-tight shadow-sm",
                a.external &&
                  "border border-dashed border-foreground/40 shadow-none",
                clickable &&
                  "cursor-pointer hover:brightness-95 focus-visible:outline-2 focus-visible:outline-ring",
              )}
              style={{
                top: a.top,
                height: a.height - 2,
                left: `calc(${a.lane * width}% + 4px)`,
                width: `calc(${width}% - 8px)`,
                backgroundColor: a.color_hex || "var(--muted-foreground)",
                opacity: a.external ? 0.5 : a.missed ? 0.7 : 1,
                color: text,
              }}
            >
              <span className="flex items-center gap-1">
                <span className="min-w-0 flex-1 truncate font-medium">
                  {a.title || "(untitled)"}
                </span>
                {a.refined && (
                  <span
                    className="shrink-0 rounded-sm bg-black/25 px-1 text-[10px] font-semibold uppercase"
                    style={{ color: text }}
                    title={(a.steps ?? [])
                      .map((s) => `${s.minutes ? `${s.minutes} min ` : ""}${s.text}`)
                      .join("\n")}
                  >
                    ✓ Planned
                  </span>
                )}
                {a.missed && (
                  <span className="shrink-0 rounded-sm bg-destructive px-1 text-[10px] font-semibold uppercase text-white">
                    Missed
                  </span>
                )}
                {(() => {
                  const links = a.template_id
                    ? linksByTemplate?.get(a.template_id)
                    : undefined;
                  if (!links || links.length === 0) return null;
                  return (
                    <a
                      href={links[0].url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={links
                        .map((l) => `${l.title}: ${l.url}`)
                        .join("\n")}
                      aria-label={`Open ${links[0].title}`}
                      onClick={(ev) => ev.stopPropagation()}
                      className="shrink-0 rounded-sm p-0.5 opacity-80 hover:opacity-100 hover:bg-black/10"
                      style={{ color: text }}
                    >
                      <LinkIcon />
                    </a>
                  );
                })()}
              </span>
              {a.height >= 34 && (
                <span className="block truncate opacity-85">
                  {new Date(a.start_at).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              )}
            </div>
          );
        })}
        {dragOverMin !== null && (
          <div
            className="pointer-events-none absolute inset-x-1 flex items-center rounded border-2 border-dashed border-primary bg-primary/10 px-2 text-xs text-primary"
            style={{
              top: ((dragOverMin - startMin) / 60) * HOUR_PX,
              height: HOUR_PX / 2,
            }}
            aria-hidden
          >
            {fmt(dragOverMin)}
          </div>
        )}
        {showNow && (
          <div
            className="pointer-events-none absolute inset-x-0 flex items-center"
            style={{ top: ((nowMin - startMin) / 60) * HOUR_PX }}
            aria-label={`Now, ${fmt(nowMin)}`}
          >
            <span className="-ml-1 h-2.5 w-2.5 rounded-full bg-destructive" />
            <span className="h-px flex-1 bg-destructive" />
          </div>
        )}
      </div>
    </div>
  );
}
